import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import { getArticleById, getSortedArticles } from '../data/articles';

export default function Article() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const article = getArticleById(articleId);
  const allArticles = getSortedArticles();

  // Find current article index and get next/prev
  const currentIndex = allArticles.findIndex(a => a.id === articleId);
  const prevArticle = currentIndex < allArticles.length - 1 ? allArticles[currentIndex + 1] : null;
  const nextArticle = currentIndex > 0 ? allArticles[currentIndex - 1] : null;

  if (!article) {
    return (
      <div className="min-h-screen" data-testid="article-not-found">
        <PublicNav currentPage="blog" />
        <section className="bg-slate-100 py-20">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h1 className="text-3xl font-bold text-[#1e3a5f] mb-4">Article Not Found</h1>
            <p className="text-[#334d6e] mb-8">The article you're looking for doesn't exist.</p>
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <ArrowLeft size={18} />
              Back to Blog
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="article-page">
      <PublicNav currentPage="blog" />

      {/* Article Header - Light */}
      <section className="bg-slate-100 pt-12 pb-8">
        <div className="max-w-3xl mx-auto px-6">
          {/* Back Link */}
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm mb-8"
            data-testid="back-to-blog-link"
          >
            <ArrowLeft size={16} />
            Back to Blog
          </Link>

          {/* Category Badge */}
          <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium mb-4 uppercase">
            {article.category}
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f] mb-6 leading-tight" data-testid="article-title">
            {article.title}
          </h1>

          {/* Meta Info */}
          <div className="flex items-center gap-6 text-[#4a6382] text-sm">
            <span className="flex items-center gap-2">
              <Calendar size={16} />
              {article.date}
            </span>
            <span className="flex items-center gap-2">
              <Clock size={16} />
              {article.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Article Body - Light */}
      <section className="bg-slate-100 pb-16">
        <div className="max-w-3xl mx-auto px-6">
          <article className="prose prose-lg max-w-none" data-testid="article-body">
            {article.body.map((paragraph, index) => (
              <p 
                key={index} 
                className="text-[#4a6382] leading-relaxed mb-6 text-base sm:text-lg"
              >
                {paragraph}
              </p>
            ))}
            
            {/* Disclaimer */}
            <div className="mt-10 pt-6 border-t border-slate-300">
              <p className="text-[#4a6382] text-sm italic">
                {article.disclaimer}
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* Article Navigation - Dark */}
      <section className="bg-slate-800 py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row justify-between gap-6">
            {/* Previous Article */}
            <div className="flex-1">
              {prevArticle && (
                <Link 
                  to={`/blog/${prevArticle.id}`}
                  className="block p-4 rounded-lg border border-slate-600 hover:border-blue-500 transition-colors group"
                  data-testid="prev-article-link"
                >
                  <span className="text-[#a3b8d1] text-xs uppercase tracking-wide">Previous</span>
                  <p className="text-[#c7d4e4] font-medium mt-1 group-hover:text-blue-400 transition-colors">
                    {prevArticle.title}
                  </p>
                </Link>
              )}
            </div>

            {/* Next Article */}
            <div className="flex-1 text-right">
              {nextArticle && (
                <Link 
                  to={`/blog/${nextArticle.id}`}
                  className="block p-4 rounded-lg border border-slate-600 hover:border-blue-500 transition-colors group"
                  data-testid="next-article-link"
                >
                  <span className="text-[#a3b8d1] text-xs uppercase tracking-wide">Next</span>
                  <p className="text-[#c7d4e4] font-medium mt-1 group-hover:text-blue-400 transition-colors">
                    {nextArticle.title}
                  </p>
                </Link>
              )}
            </div>
          </div>

          {/* Back to All Articles */}
          <div className="text-center mt-8">
            <Link 
              to="/blog"
              className="text-blue-400 hover:text-blue-300 font-medium text-sm"
            >
              View all articles
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
